import React from "react";
import LANGUAGE_DATA from "./languages.json";
// No cycle: locales/index.js imports only the JSON catalogs.
import { catalogFor } from "../locales";

/**
 * Site i18n: the supported language list, path helpers for the per-locale
 * routes, and the context that hands the active catalog to components.
 *
 * The language list is duplicated from the hezo repo's `@hezo/shared` on
 * purpose. It is *data*, and the two repos already hand-sync marketplace team
 * names the same way; coupling this build to a pinned hezo release to share a
 * twelve-item list would cost more than it saves. Keep it in step with
 * `LANGUAGE_LABELS` there when a language is added.
 */

export const DEFAULT_LANGUAGE = LANGUAGE_DATA.default;

/** Where an explicit language choice is remembered, so detection never fights it. */
export const STORAGE_KEY = "lang";

/**
 * The language the reader explicitly chose, if it is still one we ship.
 *
 * Wrapped because private browsing throws on `localStorage` access, and a code
 * left behind by a dropped language must not resurrect it.
 */
export function storedLanguage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && LANGUAGE_CODES.includes(saved) ? saved : null;
  } catch {
    return null;
  }
}

/**
 * The language list lives in `languages.json` because `gatsby-node.js` is CJS
 * and cannot import this module (it holds JSX). Both sides read the same file,
 * so the routes Gatsby generates and the languages the UI offers can never
 * disagree. The logic stays here; only the data is shared.
 *
 * The list itself is duplicated from the hezo repo's `@hezo/shared` - data, not
 * logic, hand-synced the same way marketplace team names already are.
 */
export const LANGUAGES = LANGUAGE_DATA.codes.map((code) => ({
  code,
  label: LANGUAGE_DATA.labels[code],
}));

export const LANGUAGE_CODES = LANGUAGE_DATA.codes;

/** BCP-47 values for `<html lang>` / `og:locale`. */
export const OG_LOCALES = LANGUAGE_DATA.ogLocales;

/**
 * Tags that don't reduce to a supported language by their primary subtag.
 * Traditional-script Chinese points at Simplified deliberately: the closest
 * thing the site ships, and closer than English.
 */
const TAG_ALIASES = {
  pt: "pt-BR",
  "pt-pt": "pt-BR",
  "pt-br": "pt-BR",
  zh: "zh-Hans",
  "zh-cn": "zh-Hans",
  "zh-sg": "zh-Hans",
  "zh-hans": "zh-Hans",
  "zh-hant": "zh-Hans",
  "zh-tw": "zh-Hans",
  "zh-hk": "zh-Hans",
  "zh-mo": "zh-Hans",
};

/** Resolve one BCP-47 tag to a supported language, or null. */
export function matchLanguageTag(tag) {
  const normalized = String(tag || "").trim().toLowerCase().replace(/_/g, "-");
  if (!normalized) return null;
  if (TAG_ALIASES[normalized]) return TAG_ALIASES[normalized];
  const primary = normalized.split("-")[0];
  if (TAG_ALIASES[primary]) return TAG_ALIASES[primary];
  return LANGUAGE_CODES.find((c) => c.toLowerCase() === primary) || null;
}

/** Best supported language from an ordered preference list, else English. */
export function negotiateLanguage(preferred) {
  for (const tag of preferred || []) {
    const match = matchLanguageTag(tag);
    if (match) return match;
  }
  return DEFAULT_LANGUAGE;
}

/**
 * Prefix a site-root path with the active language.
 *
 * English stays unprefixed so every existing inbound link and the current
 * sitemap keep working. External, anchor-only, and already-prefixed paths pass
 * through untouched.
 *
 * Every nav, footer, and cross-link href must go through this - otherwise a
 * German page links straight back into English and the whole locale becomes a
 * one-page dead end.
 */
export function localizedPath(path, language) {
  if (!path || typeof path !== "string") return path;
  if (!language || language === DEFAULT_LANGUAGE) return path;
  if (/^([a-z]+:)?\/\//i.test(path) || path.startsWith("#") || path.startsWith("mailto:")) {
    return path;
  }
  if (!path.startsWith("/")) return path;
  if (LANGUAGE_CODES.some((c) => c !== DEFAULT_LANGUAGE && path.startsWith(`/${c}/`))) {
    return path;
  }
  // Routes that were never localized have no prefixed twin - /de/docs/ does not
  // exist, so prefixing a docs link would produce a 404 on every locale.
  if (!isLocalizable(path)) return path;
  return `/${language}${path}`;
}

/** Strip any language prefix, giving the canonical English path. */
export function unlocalizedPath(path) {
  if (!path || typeof path !== "string") return path;
  for (const code of LANGUAGE_CODES) {
    if (code === DEFAULT_LANGUAGE) continue;
    if (path === `/${code}` || path === `/${code}/`) return "/";
    if (path.startsWith(`/${code}/`)) return path.slice(code.length + 1);
  }
  return path;
}

/**
 * Paths that are never localized - docs bodies come from another repo, and 12
 * URL copies of identical English prose would be a duplicate-content problem.
 */
export const UNLOCALIZED_PREFIXES = LANGUAGE_DATA.unlocalizedPrefixes;

/**
 * The language a built path belongs to, derived from its prefix.
 *
 * Lets `Seo` and other Head-API components resolve the language without every
 * page having to thread `pageContext` through - the URL already carries it.
 */
export function languageFromPath(path) {
  if (!path || typeof path !== "string") return DEFAULT_LANGUAGE;
  for (const code of LANGUAGE_CODES) {
    if (code === DEFAULT_LANGUAGE) continue;
    if (path === `/${code}` || path.startsWith(`/${code}/`)) return code;
  }
  return DEFAULT_LANGUAGE;
}

export function isLocalizable(path) {
  return !UNLOCALIZED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

/**
 * Look a key up and substitute `{placeholder}` tokens.
 *
 * Interpolation rather than concatenating two half-strings: where a value
 * lands inside a sentence differs by language (Japanese puts the read time
 * before the unit, German appends "Lesezeit"), and gluing fragments together
 * would freeze English word order into all twelve catalogs.
 */
function translate(catalog, key, vars) {
  const value = (catalog && catalog[key]) || key;
  if (!vars) return value;
  return Object.keys(vars).reduce(
    (acc, name) => acc.split(`{${name}}`).join(String(vars[name])),
    value
  );
}

/**
 * Split a translated string on `{token}` placeholders and interleave React
 * nodes - a link, a monospaced span - at those positions.
 *
 * Where such an element falls inside a sentence differs by language (Japanese
 * fronts the object, German pushes the verb to the end), so the catalog carries
 * the whole sentence with tokens in it and the nodes are woven in here. The
 * alternative - concatenating a prefix, the element, and a suffix - freezes
 * English word order into all twelve catalogs.
 *
 * Third call site of this pattern (the CTA note, the telemetry tooltip, the
 * homepage FAQ), so it lives here rather than being re-derived per page.
 */
export function interpolateNodes(template, nodes) {
  const names = Object.keys(nodes);
  if (names.length === 0) return template;
  const pattern = new RegExp(`\\{(${names.join("|")})\\}`, "g");
  const out = [];
  let last = 0;
  let match = pattern.exec(template);
  while (match) {
    if (match.index > last) out.push(template.slice(last, match.index));
    out.push(React.cloneElement(nodes[match[1]], { key: `${match[1]}-${match.index}` }));
    last = match.index + match[0].length;
    match = pattern.exec(template);
  }
  if (last < template.length) out.push(template.slice(last));
  return out;
}

const I18nContext = React.createContext({
  language: DEFAULT_LANGUAGE,
  t: (key) => key,
  localize: (path) => path,
});

/**
 * The language comes from `pageContext`, not from state: the routes are static,
 * so the page Gatsby served already *is* the right language. Switching
 * navigates to the sibling path rather than swapping strings in place, which
 * keeps the URL, the rendered HTML, and `<html lang>` in agreement.
 *
 * Docs are the one exception. They live only at the unprefixed `/docs/*`
 * (their bodies come from another repo, and twelve URL copies of identical
 * English prose would be a duplicate-content problem), so pageContext always
 * says `en` there and the surrounding chrome - search, OS tabs, copy buttons -
 * would stay English no matter what the reader picked. For those paths only,
 * the stored preference takes over after mount: the server-rendered HTML still
 * says English so hydration matches, then the chrome switches. Same
 * `mounted`-flag shape as ThemeToggle and the language switcher.
 */
export function I18nProvider({ language, children }) {
  const pageLanguage = language || DEFAULT_LANGUAGE;
  const [stored, setStored] = React.useState(null);

  React.useEffect(() => {
    // A localizable path already carries its language in the URL - leave it be.
    if (isLocalizable(window.location.pathname)) return;
    // Null when nothing is stored or storage is blocked, and the page then
    // stays in its served language.
    setStored(storedLanguage());
  }, []);

  const active = stored || pageLanguage;
  const value = React.useMemo(
    () => ({
      language: active,
      t: (key, vars) => translate(catalogFor(active), key, vars),
      localize: (path) => localizedPath(path, active),
    }),
    [active]
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return React.useContext(I18nContext);
}
